import React from 'react';
import styled from 'styled-components';
import { Body7, VisuallyHidden } from '@ui';
import { GhostButton } from '@ui/buttons';
import { payloadEntries } from './format';
import { PayloadField } from './PayloadFields';

// Aligned with the row's own 8px gutter so the disclosure reads as part of the row above it rather
// than as a floating block.
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 8px 10px;
`;

// A left rule instead of a filled panel: nothing in this table paints a ground, and the rule does
// the same job of setting the prose apart from the grid.
const Panel = styled.div`
  border-left: 2px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  padding: 2px 0 2px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Toggle = styled(GhostButton)`
  color: ${({ theme }) => theme.themeColors.primaryCtaButton};
`;

// A single-input feed's rows carry the score spec's declared payload columns — the reasoning behind
// the score. Kept behind a disclosure rather than made into columns: `explanation` and `thinking`
// are paragraphs, and a column of paragraphs would destroy the row height, the frozen column and
// any hope of scanning the table.
//
// Field names are declared per score spec, so whatever the row carries is rendered, in name order.
// A fixed explanation/thinking pair would silently drop the first spec that declares a third.
export const RowPayload = ({ payload, rowLabel }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const entries = payloadEntries(payload);

  if (entries.length === 0) return null;

  // The toggle sits below the prose: expanded, "Hide reasoning" is where the reader ends up rather
  // than back at the top of what they just read.
  return (
    <Wrapper>
      {isOpen && (
        <Panel>
          {entries.map((entry) => (
            <Field key={entry.key}>
              <PayloadField entry={entry} />
            </Field>
          ))}
        </Panel>
      )}
      <Toggle type="button" onClick={() => setIsOpen((prev) => !prev)} aria-expanded={isOpen}>
        <Body7 bold>{isOpen ? 'Hide reasoning' : `Reasoning (${entries.length})`}</Body7>
        {rowLabel ? <VisuallyHidden>{` for ${rowLabel}`}</VisuallyHidden> : null}
      </Toggle>
    </Wrapper>
  );
};
